import React, { useRef } from 'react';
import Editor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';

interface SqlEditorProps {
  value: string;
  onChange: (value: string) => void;
  height?: string;
  tableSuggestions?: Array<{
    tableName: string;
    columns: Array<{ name: string; type: string }>;
  }>;
}

const SqlEditor: React.FC<SqlEditorProps> = ({
  value,
  onChange,
  height = '200px',
  tableSuggestions = [],
}) => {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  const handleEditorDidMount = (editor: editor.IStandaloneCodeEditor, monaco: any) => {
    editorRef.current = editor;

    // Register custom SQL completions based on database schema
    if (tableSuggestions.length > 0) {
      const completionProvider = monaco.languages.registerCompletionItemProvider('sql', {
        provideCompletionItems: (_model: any, _position: any) => {
          const suggestions: any[] = [];

          // Table name suggestions
          tableSuggestions.forEach((table) => {
            suggestions.push({
              label: table.tableName,
              kind: monaco.languages.CompletionItemKind.Class,
              insertText: table.tableName,
              detail: 'Table',
              documentation: `Table with ${table.columns.length} columns`,
            });

            // Column suggestions for each table
            table.columns.forEach((column) => {
              suggestions.push({
                label: `${table.tableName}.${column.name}`,
                kind: monaco.languages.CompletionItemKind.Field,
                insertText: `${table.tableName}.${column.name}`,
                detail: `${column.type}`,
                documentation: `Column from ${table.tableName}`,
              });

              // Also suggest just column name
              suggestions.push({
                label: column.name,
                kind: monaco.languages.CompletionItemKind.Field,
                insertText: column.name,
                detail: `${column.type} (${table.tableName})`,
              });
            });
          });

          // Common SQL keywords
          const keywords = [
            'SELECT', 'FROM', 'WHERE', 'JOIN', 'INNER JOIN', 'LEFT JOIN', 
            'RIGHT JOIN', 'ON', 'AND', 'OR', 'ORDER BY', 'GROUP BY', 
            'HAVING', 'LIMIT', 'OFFSET', 'INSERT INTO', 'UPDATE', 'DELETE',
            'CREATE TABLE', 'DROP TABLE', 'ALTER TABLE', 'COUNT', 'SUM',
            'AVG', 'MAX', 'MIN', 'AS', 'DISTINCT', 'TOP', 'UNION',
          ];

          keywords.forEach((keyword) => {
            suggestions.push({
              label: keyword,
              kind: monaco.languages.CompletionItemKind.Keyword,
              insertText: keyword,
              detail: 'SQL Keyword',
            });
          });

          return { suggestions };
        },
      });

      // Cleanup on unmount
      return () => completionProvider.dispose();
    }
  };

  const handleChange = (value: string | undefined) => {
    onChange(value || '');
  };

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: '4px', overflow: 'hidden' }}>
      <Editor
        height={height}
        defaultLanguage="sql"
        value={value}
        onChange={handleChange}
        onMount={handleEditorDidMount}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          lineNumbers: 'on',
          roundedSelection: true,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          formatOnPaste: true,
          formatOnType: true,
          suggest: {
            showKeywords: true,
            showSnippets: true,
          },
          quickSuggestions: {
            other: true,
            comments: false,
            strings: false,
          },
          parameterHints: {
            enabled: true,
          },
          acceptSuggestionOnCommitCharacter: true,
          acceptSuggestionOnEnter: 'on',
          tabCompletion: 'on',
        }}
      />
    </div>
  );
};

export default SqlEditor;
